import type { CancelToken } from 'axios';
import { Dictionary } from 'redux-ngrx-entity';
import { RemoteSensingApi } from '../../core/api';
import type { CursorDTO } from '../../core/models';
import type { ImageAOI, ImageAOI_DTO, SceneGroup } from '../../core/models/imageAssets';
import { Source } from '../../core/models/imageAssets';
import dayjs from '../../utils/dateUtils';

export const PLANET_VISUAL_BACKGROUND_MAX_AGE_DAYS = 60;
export const PLANET_VISUAL_BACKGROUND_MAX_CLOUD_PERCENT = 20;

// Sentinel Visual (RGB) background: most recent 0%-cloud, then most recent within the cloud
// ceiling, among Sentinel scenes in the maxAgeDays before referenceDate. Both bounds are
// LaunchDarkly-tunable; maxCloudPercent is an integer percent of the 0–1 imageCloudCoverage.
export const selectPlanetVisualBackgroundFromGroups = (
  groups: SceneGroup[],
  referenceDate: string | Date | undefined,
  maxAgeDays: number = PLANET_VISUAL_BACKGROUND_MAX_AGE_DAYS,
  maxCloudPercent: number = PLANET_VISUAL_BACKGROUND_MAX_CLOUD_PERCENT
): ImageAOI | null => {
  if (!referenceDate) return null;

  const reference = dayjs(referenceDate);
  const cutoff = reference.subtract(maxAgeDays, 'day');
  const candidates = groups
    .map((group) => group.referenceImage)
    .filter(
      (image) =>
        image.source === Source.SENTINEL &&
        image.subProducts.includes('rgb') &&
        dayjs(image.acquiredAt).isAfter(cutoff) &&
        !dayjs(image.acquiredAt).isAfter(reference)
    )
    .sort((a, b) => Date.parse(b.acquiredAt) - Date.parse(a.acquiredAt));

  const maxCloudCoverage = maxCloudPercent / 100;

  return (
    candidates.find((image) => image.imageCloudCoverage === 0) ??
    candidates.find((image) => image.imageCloudCoverage <= maxCloudCoverage) ??
    null
  );
};

type FetchImagesByAOIOptions = {
  startDate: string;
  endDate: string;
  aoiType: string;
  sources: 'all';
  pageSize: number;
  loadComposedOf: boolean;
  cursor?: string | null;
  cancelToken?: CancelToken;
};

export const updateReferenceImage = (
  referenceImage: ImageAOI,
  loadedImages: ReadonlyArray<ImageAOI>
): ImageAOI => {
  const fieldImages = loadedImages.filter((image) => image.sceneId === referenceImage.sceneId);
  const imageCloudCoverage = fieldImages?.length
    ? fieldImages.reduce<number>((acc, image) => {
        return acc + image.imageCloudCoverage;
      }, 0) / fieldImages.length
    : 1;

  const newComposedOf = fieldImages.reduce<Dictionary<ImageAOI>>((acc, image) => {
    return {
      ...acc,
      [`field:${image?.aoi?.uid}`]: image,
    };
  }, {});
  return {
    ...referenceImage,
    imageCloudCoverage,
    composedOf: newComposedOf,
  };
};

export const groupImagesByDate = (images: ImageAOI[]): SceneGroup[] => {
  const groups = new Map<string, SceneGroup>();

  images.forEach((image) => {
    // Try to find an existing group for this images, if there is
    // none, create a new group

    const groupId = `${dayjs(image.acquiredAt).startOf('day').toString()}-${image.source}`;

    const group = groups.get(groupId);
    if (group) {
      // Group found, just add the image to it & update group values if needed
      const shouldReplaceReference = group.images.some(
        (item) => item.sceneCloudCoverage > image.sceneCloudCoverage
      );
      if (shouldReplaceReference) {
        group.referenceImage = image;
        group.id = image.id;
      }
      group.images.push(image);
      // Make sure imageAssets are ordered by date
      group.images.sort((a, b) => Date.parse(b.acquiredAt) - Date.parse(a.acquiredAt));
    } else {
      // No group found, create a new one
      groups.set(groupId, {
        id: image.id,
        images: [image],
        referenceImage: image,
      });
    }
  });

  return Array.from(groups, ([_, group]) => group);
};

export const fetchScenesByAOI = (aoiId: string, options: FetchImagesByAOIOptions) => {
  return RemoteSensingApi.get<CursorDTO<ImageAOI_DTO>>('/aois/images', {
    params: {
      uid: aoiId,
      aoi_type: options.aoiType,
      start_date: options.startDate,
      end_date: options.endDate,
      sources: options.sources,
      cursor: options.cursor ?? undefined,
      size: options.pageSize,
      add_scene_bounds: true,
      ...(options.loadComposedOf ? {} : { exclude: 'composed_of' }),
    },
    cancelToken: options.cancelToken,
  });
};

export const mergeSceneGroup = (scene1: SceneGroup, scene2: SceneGroup): SceneGroup => {
  const imageIds = new Set(scene1.images.map(({ id }) => id));
  const imagesToMerge = scene2.images.filter(({ id }) => !imageIds.has(id));

  return {
    ...scene1,
    images: scene1.images.concat(imagesToMerge),
  };
};

export const zeroPad = (index: number, max: number = 10) => {
  return (index + 1 < max ? '0' : '') + (index + 1);
};
